import type { StarSummary } from "@howcomet/shared";

/**
 * Where every star in one constellation sits.
 *
 * A radial tree: each root owns an angular wedge sized by how much of the
 * constellation hangs off it, children divide their parent's, and depth is
 * distance from the centre. Edges inside one wedge cannot cross edges in
 * another, so the picture is the shape of the child's own exploration.
 *
 * The wandering is not decoration. The first version put every node at the
 * exact midpoint of its wedge, which meant a chain of six follow-ups came out
 * as six collinear points — a ruler-drawn line, and nothing in the sky looks
 * like that. Each star now leans off the midpoint and sits at a slightly
 * different distance, and a lone child re-centres on where its parent actually
 * ended up, so a chain meanders instead of marching.
 *
 * Every offset is derived from the star's own topic id, so it is stable: the
 * same star lands in the same place on every render and on every device, with
 * no state stored anywhere.
 */

/** Distance between depth rings, before the per-star variation. */
const RING = 96;

/** The most a star may lean off its wedge midpoint. About 26°. */
const MAX_LEAN = 0.46;

/** How much of its own wedge a star may use up leaning, so siblings stay apart. */
const LEAN_SHARE = 0.7;

/** How much a star may drift in or out of its ring. Never a full step. */
const RING_DRIFT = 0.24;

/** Arc room each independent star needs on the first ring. */
const ROOT_SPACING = 72;

export function layoutConstellation(stars: StarSummary[]): [number, number][] {
  const indexOf = new Map(stars.map((star, i) => [star.topicId, i]));
  const children: number[][] = stars.map(() => []);
  const roots: number[] = [];

  stars.forEach((star, i) => {
    // A parent outside this constellation is no parent here — categories can
    // differ along a thread, and half an edge is worse than none.
    const parent = star.parentTopicId ? indexOf.get(star.parentTopicId) : undefined;
    if (parent === undefined || parent === i) roots.push(i);
    else children[parent]!.push(i);
  });

  /*
    Cycles should be impossible — a follow-up is always created after its
    parent — but this walks data from a database, and a layout that recurses
    forever takes the whole screen with it.
  */
  const seen = new Set<number>();
  const leaves: number[] = stars.map(() => 1);
  const countLeaves = (i: number): number => {
    if (seen.has(i)) return 0;
    seen.add(i);
    const kids = children[i]!.filter((k) => !seen.has(k));
    children[i] = kids;
    if (kids.length === 0) return (leaves[i] = 1);
    return (leaves[i] = kids.reduce((total, k) => total + countLeaves(k), 0));
  };
  for (const root of roots) countLeaves(root);
  // Anything left unvisited was in a cycle. It still needs somewhere to be.
  stars.forEach((_, i) => {
    if (!seen.has(i)) {
      seen.add(i);
      roots.push(i);
      leaves[i] = 1;
      children[i] = [];
    }
  });

  const points: [number, number][] = stars.map(() => [0, 0]);

  /*
    The first ring grows with how many independent stars have to sit on it.
    Fixed at one ring, a subject with two dozen unrelated questions crowded
    them all onto a circle 600 long and they overlapped — which is exactly the
    collision the old `PATTERN[i % 8]` layout had, arriving by a different
    road. A test builds 24 rootless stars and measures the closest pair.
  */
  const rootRing =
    roots.length > 1 ? Math.max(RING, (ROOT_SPACING * roots.length) / (Math.PI * 2)) : RING;

  const place = (i: number, depth: number, from: number, to: number) => {
    const span = to - from;
    const id = stars[i]!.topicId;

    /*
      Lean off the midpoint, bounded twice: by a fixed angle so a lone chain
      with the whole circle to itself does not swing wildly, and by a share of
      this star's own wedge so it can never reach a sibling's.
    */
    const room = Math.min(span * LEAN_SHARE, MAX_LEAN * 2) / 2;
    const angle = (from + to) / 2 + (unitFrom(id, "lean") - 0.5) * 2 * room;

    /*
      Drift is added to the ring rather than multiplied by it, so it cannot
      grow with the root ring and overtake the next step. A star moves by at
      most 0.12 of a ring and every step out is a whole one, which makes "a
      follow-up is always further out than its parent" true by arithmetic
      rather than by hoping the numbers stay small.
    */
    const drift = (unitFrom(id, "ring") - 0.5) * RING * RING_DRIFT;
    const radius = rootRing + (depth - 1) * RING + drift;
    points[i] = depth === 0 ? [0, 0] : [Math.cos(angle) * radius, Math.sin(angle) * radius];

    const kids = children[i]!;
    if (kids.length === 0) return;

    /*
      Children are dealt out around where this star actually ended up rather
      than around the midpoint it was allocated, which is what makes a chain
      follow its parent instead of springing back to the axis. Clamped to the
      wedge this star was given, so the meander bounces off its own walls
      rather than wandering into another branch.
    */
    const childFrom = Math.max(from, angle - span / 2);
    const childTo = Math.min(to, angle + span / 2);
    const childSpan = childTo - childFrom;

    let cursor = childFrom;
    for (const kid of kids) {
      const share = (childSpan * leaves[kid]!) / leaves[i]!;
      place(kid, depth + 1, cursor, cursor + share);
      cursor += share;
    }
  };

  const TAU = Math.PI * 2;
  if (roots.length === 1) {
    place(roots[0]!, 0, 0, TAU);
  } else {
    const total = roots.reduce((sum, r) => sum + leaves[r]!, 0) || 1;
    let cursor = 0;
    for (const root of roots) {
      const share = (TAU * leaves[root]!) / total;
      // Rooted on the first ring rather than the centre: several independent
      // questions in one subject have no hub, and inventing one would claim a
      // relationship the child never made.
      place(root, 1, cursor, cursor + share);
      cursor += share;
    }
  }
  return points;
}

/**
 * A stable number in [0, 1) for one star and one purpose.
 *
 * FNV-1a over the topic id. Deterministic on purpose: a star that moved every
 * time the sky re-rendered would be a different bug from the one this fixes,
 * and a remembered position would be state to store and keep in step.
 */
function unitFrom(id: string, salt: string): number {
  let hash = 0x811c9dc5;
  const text = `${salt}:${id}`;
  for (let i = 0; i < text.length; i += 1) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash / 0x100000000;
}

/**
 * Which side of its star a label sits on.
 *
 * Every label used to hang below, so two stars near the same height overprinted
 * each other's names. Alternating halves that without moving a single star, and
 * being derived from the id it never flickers between renders.
 */
export function labelBelow(id: string): boolean {
  return unitFrom(id, "label") < 0.5;
}

/**
 * The thread from a parent to its follow-up, bowed slightly to one side.
 *
 * Straight segments between wandering stars still read as a technical diagram.
 * A constant, per-edge curve is what makes it look drawn rather than plotted —
 * and the bow is derived from the child's id, so it never changes.
 */
export function threadPath(
  from: readonly [number, number],
  to: readonly [number, number],
  id: string,
): string {
  const [x1, y1] = from;
  const [x2, y2] = to;
  const midX = (x1 + x2) / 2;
  const midY = (y1 + y2) / 2;
  const dx = x2 - x1;
  const dy = y2 - y1;
  const length = Math.hypot(dx, dy) || 1;
  // Perpendicular, at up to a tenth of the span. Enough to read as a curve at
  // a glance and not enough to make a short thread look like a detour.
  const bow = (unitFrom(id, "bow") - 0.5) * length * 0.2;
  const cx = midX + (-dy / length) * bow;
  const cy = midY + (dx / length) * bow;
  return `M ${x1} ${y1} Q ${cx} ${cy} ${x2} ${y2}`;
}
